import { router } from "@inertiajs/react"
import { BackofficeLayout } from "@/components/layout/BackofficeLayout"
import { DetailGrid } from "@/components/backoffice/DetailGrid"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useConfirm } from "@/components/ui/confirm-dialog"
import { CheckCircle, XCircle, ShieldOff, ShieldCheck } from "lucide-react"
import type { NotaryDetailed } from "@/types"

function formatDate(date: string): string {
  if (!date) return "\u2014"
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

function StatusBadge({ status }: { status: NotaryDetailed['status'] }) {
  const config: Record<string, { label: string; className: string }> = {
    pending: { label: "Pending", className: "bg-yellow-100 text-yellow-800" },
    approved: { label: "Approved", className: "bg-green-100 text-green-800" },
    rejected: { label: "Rejected", className: "bg-red-100 text-red-800" },
    suspended: { label: "Suspended", className: "bg-gray-100 text-gray-800" },
  }
  const { label, className } = config[status] || { label: status, className: "bg-secondary text-secondary-foreground" }
  return <Badge className={`font-normal text-xs ${className}`}>{label}</Badge>
}

type Props = {
  notary: NotaryDetailed
}

export default function BackofficeNotariesShow({ notary }: Props) {
  const confirm = useConfirm()

  const handleApprove = async () => {
    if (!(await confirm({ title: "Approve Notary", description: `Approve ${notary.full_name} as a notary?`, confirmLabel: "Approve" }))) return
    router.post(`/backoffice/notaries/${notary.id}/approval`)
  }

  const handleReject = async () => {
    if (!(await confirm({ title: "Reject Notary", description: `Reject ${notary.full_name}'s notary application?`, confirmLabel: "Reject", destructive: true }))) return
    router.post(`/backoffice/notaries/${notary.id}/rejection`)
  }

  const handleSuspend = async () => {
    if (!(await confirm({ title: "Suspend Notary", description: `Suspend ${notary.full_name}? They will not be able to accept new appointments.`, confirmLabel: "Suspend", destructive: true }))) return
    router.post(`/backoffice/notaries/${notary.id}/suspension`)
  }

  const handleUnsuspend = async () => {
    if (!(await confirm({ title: "Unsuspend Notary", description: `Reinstate ${notary.full_name}? They will be able to accept appointments again.`, confirmLabel: "Unsuspend" }))) return
    router.delete(`/backoffice/notaries/${notary.id}/suspension`)
  }

  return (
    <BackofficeLayout title={notary.full_name} breadcrumbs={[{ label: "Backoffice", href: "/backoffice/dashboard" }, { label: "Notaries", href: "/backoffice/notaries" }, { label: notary.full_name }]}>
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{notary.full_name}</h1>
            <StatusBadge status={notary.status} />
          </div>
          <p className="text-sm text-muted-foreground">{notary.email_address}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {notary.status === 'pending' && (
            <>
              <Button size="sm" variant="outline" onClick={handleApprove} className="h-8 text-[13px]">
                <CheckCircle className="mr-1.5 h-3 w-3" />
                Approve
              </Button>
              <Button size="sm" variant="ghost" onClick={handleReject} className="h-8 text-[13px] text-destructive hover:text-destructive">
                <XCircle className="mr-1.5 h-3 w-3" />
                Reject
              </Button>
            </>
          )}
          {notary.status === 'approved' && (
            <Button size="sm" variant="ghost" onClick={handleSuspend} className="h-8 text-[13px] text-destructive hover:text-destructive">
              <ShieldOff className="mr-1.5 h-3 w-3" />
              Suspend
            </Button>
          )}
          {notary.status === 'suspended' && (
            <Button size="sm" variant="outline" onClick={handleUnsuspend} className="h-8 text-[13px]">
              <ShieldCheck className="mr-1.5 h-3 w-3" />
              Unsuspend
            </Button>
          )}
        </div>

        {/* Profile Info */}
        <DetailGrid items={[
          { label: 'Email', value: notary.email_address },
          { label: 'Phone', value: notary.phone || '\u2014' },
          { label: 'Commission Number', value: notary.commission_number },
          { label: 'Commission State', value: notary.commission_state },
          { label: 'Commission Expiry', value: formatDate(notary.commission_expiry) },
          { label: 'Session Modes', value: notary.session_modes.join(', ') || '\u2014' },
          { label: 'Appointments', value: `${notary.appointments_count}` },
          { label: 'Acts', value: `${notary.acts_count}` },
          { label: 'Joined', value: formatDate(notary.created_at) },
        ]} />

        {notary.bio && (
          <div className="py-3 border-b border-border/40">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Bio</p>
            <p className="text-sm">{notary.bio}</p>
          </div>
        )}

        {/* Approval Details */}
        {notary.approval && (
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Approval</h2>
              <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                <CheckCircle className="w-3 h-3" />
                Approved
              </span>
            </div>
            <div className="px-4 py-3 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Approved By</span>
                <span>{notary.approval.approved_by_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Approved On</span>
                <span>{formatDate(notary.approval.created_at)}</span>
              </div>
            </div>
          </section>
        )}

        {/* Rejection Details */}
        {notary.rejection && (
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Rejection</h2>
              <Badge className="bg-red-100 text-red-700 font-normal text-[11px]">Rejected</Badge>
            </div>
            <div className="px-4 py-3 space-y-1.5 text-sm">
              {notary.rejection.reason && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reason</span>
                  <span>{notary.rejection.reason}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Rejected On</span>
                <span>{formatDate(notary.rejection.created_at)}</span>
              </div>
            </div>
          </section>
        )}

        {/* Suspension Details */}
        {notary.suspension && (
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Suspension</h2>
              <Badge className="bg-gray-100 text-gray-800 font-normal text-[11px]">Suspended</Badge>
            </div>
            <div className="px-4 py-3 space-y-1.5 text-sm">
              {notary.suspension.reason && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reason</span>
                  <span>{notary.suspension.reason}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Suspended On</span>
                <span>{formatDate(notary.suspension.created_at)}</span>
              </div>
            </div>
          </section>
        )}
      </div>
    </BackofficeLayout>
  )
}
